import { OWDataType } from "./OWDataType";

/**
 * Metadata popisující strukturu aplikační třídy.
 * @includeToDoc
 */
export interface IBaseOWClassDescription {
    /** Skutečný název třídy */
    readonly className: string;
    /** Původní název třídy, pokud byla třída implementačně nahrazena jinou třídou */
    readonly originalClassName: string;
    /** Třídy existujících potomků */
    readonly childClasses: readonly string[];
    /** Třídy, že kterých tato třídá skládá (např. u mixinů nebo mapovacích tříd) */
    readonly relatedClasses: readonly string[];
    /** Udává, zda je třída perzistentí, tedy zda se instance této třídy budou ukládat do perzistetního (typicky databázového) úložiště */
    readonly isPersistent: boolean;
    /** Informace o tom, zda je povoleno vytváření digitálních podpisů */
    readonly signingAllowed: boolean;
    /** Informace o tom, zda je povoleno šifrování */
    readonly encryptingAllowed: boolean;
    /** Informace o tom, zda je povoleno verzování */
    readonly versioningAllowed: boolean;
    /** Informace o tom, zda třída podporuje kontexty */
    readonly supportsContext: boolean;
    /** Informace o tom, zda třída podporuje zobrazení včetně podřízených záznamů */
    readonly supportsHierarchy: boolean;
    /** Při šifrování kompozice (několika tříd jako jednoho celku) udává cestu k hlavnímu objektu kompozice, ke kterému je uloženo společné heslo */
    readonly passwordMasterLink: string | null;
    /** Vrací seznam všech aplikačních atributů dané třídy */
    readonly attributes: readonly OWAttributeDesciption[];
}

/**
 * Metadata popisující atribut (libovolného typu) aplikační třídy.
 * @includeToDoc
 */
export type OWAttributeDesciption = IOWAttributeDesciption | IOWClassAttributeDescription | IOWEnumAttributeDescription | IOWTextAttributeDescription;

/**
 * Metadata popisující aplikační atribut aplikační třídy.
 * @includeToDoc
 */
export interface IOWAttributeDesciption {
    /** Název atributu, property */
    readonly attributeName: string;
    /** Datový typu atributu */
    readonly type: OWDataType;
    /** Zda je atribut povinný */
    readonly required?: boolean;
    /** Zda slouží pouze ke čtení */
    readonly readOnly?: boolean;
    /** Zda má atribut nastavenou podporu pro podepisování */
    readonly signingAllowed?: boolean;
    /** Zda má atribut nastavenou podporu pro šifrování */
    readonly encryptingAllowed?: boolean;
    /** Zda atribut podporuje šifrované hodnoty */
    readonly supportsEncValue?: boolean;
    /** Název vnitřního atributu, který je určený k zobrazení (např. LocalizedText - Text) */
    readonly displayAttribute?: string;

    /** Určuje, zda atribut podporuje filtrování IS NULL a IS NOT NULL */
    readonly canFilterNull?: boolean;
    /** Určuje, zda atribut podporuje filtrování porovnáváním */
    readonly canFilterCompare?: boolean;
    /** Určuje, zda atribut podporuje filtrování rovností */
    readonly canFilterEqual?: boolean;
    /** Určuje, zda atribut podporuje filtrování pomocí operátoru LIKE */
    readonly canFilterLike?: boolean;
    /** Určuje, zda je možné podle atributu řadit */
    readonly canSort?: boolean;
    /** Určuje, zda je možné atribut seskupovat podle stejných hodnot */
    readonly canGroup?: boolean;
    /** Určuje, zda je možné získat pro atribut jen unikátní hodnoty  */
    readonly canDistinct?: boolean;
}

/**
 * Metadata popisující vazební atribut aplikační třídy.
 * @includeToDoc
 */
export interface IOWClassAttributeDescription extends IOWAttributeDesciption {
    /** Název aplikační třídy. */
    readonly className?: string;
    /** Typ agregace vazby. */
    readonly aggregation?: Aggregation;
    /** Multiplicita vazby. */
    readonly minMultiplicity?: number;
}

/**
 * Metadata popisující enumový atribut aplikační třídy.
 * @includeToDoc
 */
export interface IOWEnumAttributeDescription extends IOWAttributeDesciption {
    /** Název enumu. */
    readonly enumName?: string;
}

/**
 * Metadata popisující textový atribut aplikační třídy. Nemusí se jednat přímo o typ Text.
 * @includeToDoc
 */
export interface IOWTextAttributeDescription extends IOWAttributeDesciption {
    /** Maximální délka řetezce, která se do atributu vejde. */
    readonly size?: number;
}

/**
 * Typ agregace vazby mezi třídami.
 * @includeToDoc
 */
export enum Aggregation {
    /** bez agregace */
    none = 0,
    /** kompozitní agregace */
    composite = 1,
    /** sdílená agregace */
    shared = 2,
}

/**
 * Popis vlastností atributu aplikační třídy.
 * @includeToDoc
 */
export interface IPublicAttributeInfo {
    /** Název atributu */
    readonly attributeName: string;
    /** Datový typu atributu */
    readonly type: OWDataType;
    /** Název typu enumu, v případě OWObjectu název jeho třídy. */
    readonly additionalTypeName?: string;
    /** Název interního atributu určeného k zobrazení */
    readonly displayAttributeName?: string;
    /** Název datového typu interního atributu určeného k zobrazení */
    readonly displayAttributeType?: string;
    /** Formát, který se má použít po zobrazení hodnoty atributu */
    readonly displayFormat?: string;
    /** Příznak, zda se má atribut šifrovat */
    readonly encryptingAlowed?: boolean;
    /** Značí zda šifrovaný atribut opravdu může obsahovat šifrovanou hodnotu */
    readonly supportsEncValue?: boolean;
    /** Maximální délka hodnoty atributu */
    readonly size?: number;

    /** Určuje, zda atribut podporuje filtrování IS NULL a IS NOT NULL */
    readonly canFilterNull?: boolean;
    /** Určuje, zda atribut podporuje filtrování porovnáváním */
    readonly canFilterCompare?: boolean;
    /** Určuje, zda atribut podporuje filtrování rovností */
    readonly canFilterEqual?: boolean;
    /** Určuje, zda atribut podporuje filtrování pomocí operátoru LIKE */
    readonly canFilterLike?: boolean;
    /** Určuje, zda je možné podle atributu řadit */
    readonly canSort?: boolean;
    /** Určuje, zda je možné atribut seskupovat podle stejných hodnot */
    readonly canGroup?: boolean;
    /** Určuje, zda je možné získat pro atribut jen unikátní hodnoty  */
    readonly canDistinct?: boolean;
}
